import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { FiPackage, FiCalendar, FiHome, FiLogOut, FiUser, FiBell, FiMenu, FiX, FiUsers, FiSettings, FiInfo, FiFileText, FiGlobe } from 'react-icons/fi';
import axios from 'axios';
import { auth } from '../api';
import AppFooter from './AppFooter';
import { getVersionLabel } from '../constants/appInfo';

function Layout({ user, setUser }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    loadNotifications();
    // Обновлять уведомления каждые 30 секунд
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const API_URL = process.env.REACT_APP_API_URL || 
    (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:8000');

  const loadNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data);
      setUnreadCount(response.data.filter(n => !n.is_read).length);
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API_URL}/notifications/${notificationId}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadNotifications();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API_URL}/notifications/read-all`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadNotifications();
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleLogout = async () => {
    await auth.logout();
    localStorage.removeItem('token');
    setUser(null);
    navigate('/login');
  };

  const isActive = (path) => {
    return location.pathname === path ? 'bg-primary-600 text-white' : 'text-gray-300 hover:bg-gray-700';
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Navbar */}
      <nav className="bg-gray-800 shadow-lg">
        <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12">
          <div className="flex justify-between h-16">
            <div className="flex">
              <div className="flex-shrink-0 flex items-center gap-2">
                <span className="text-xl font-bold text-white">АКМЕТРОН Склад</span>
                <span
                  className="hidden sm:inline text-xs font-normal text-gray-400 border border-gray-600 rounded px-2 py-0.5"
                  title="Версия приложения"
                >
                  {getVersionLabel()}
                </span>
              </div>
              {/* Desktop Navigation */}
              <div className="hidden xl:ml-6 xl:flex xl:space-x-2">
                <Link
                  to="/"
                  className={`${isActive('/')} inline-flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors`}
                >
                  <FiHome className="mr-2" />
                  Главная
                </Link>
                <Link
                  to="/equipment"
                  className={`${isActive('/equipment')} inline-flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors`}
                >
                  <FiPackage className="mr-2" />
                  Оборудование
                </Link>
                <Link
                  to="/partners"
                  className={`${isActive('/partners')} inline-flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors`}
                >
                  <FiGlobe className="mr-2" />
                  Партнёры
                </Link>
                <Link
                  to="/bookings"
                  className={`${isActive('/bookings')} inline-flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors`}
                >
                  <FiCalendar className="mr-2" />
                  Бронирования
                </Link>
                {user.is_admin && (
                  <Link
                    to="/users"
                    className={`${isActive('/users')} inline-flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors`}
                  >
                    <FiUsers className="mr-2" />
                    Пользователи
                  </Link>
                )}
                {user.is_admin && (
                  <Link
                    to="/notifications"
                    className={`${isActive('/notifications')} inline-flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors`}
                  >
                    <FiBell className="mr-2" />
                    Уведомления
                  </Link>
                )}
                {user.is_admin && (
                  <Link
                    to="/logs"
                    className={`${isActive('/logs')} inline-flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors`}
                  >
                    <FiFileText className="mr-2" />
                    Логи
                  </Link>
                )}
                <Link
                  to="/about"
                  className={`${isActive('/about')} inline-flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors`}
                >
                  <FiInfo className="mr-2" />
                  О программе
                </Link>
              </div>
            </div>
            <div className="flex items-center">
              {/* Mobile menu button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="xl:hidden text-gray-300 hover:text-white p-2 rounded-md transition-colors"
              >
                {mobileMenuOpen ? <FiX className="h-6 w-6" /> : <FiMenu className="h-6 w-6" />}
              </button>
              {/* Notifications */}
              <div className="relative mr-4">
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="relative text-gray-300 hover:text-white p-2 rounded-md transition-colors"
                >
                  <FiBell className="h-6 w-6" />
                  {unreadCount > 0 && (
                    <span className="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-600 rounded-full">
                      {unreadCount}
                    </span>
                  )}
                </button>
                
                {/* Notifications dropdown */}
                {showNotifications && (
                  <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
                    <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
                      <h3 className="text-sm font-semibold text-gray-900">
                        Уведомления ({unreadCount})
                      </h3>
                      {unreadCount > 0 && (
                        <button
                          onClick={markAllAsRead}
                          className="text-xs text-primary-600 hover:text-primary-700"
                        >
                          Отметить все
                        </button>
                      )}
                    </div>
                    
                    {notifications.length === 0 ? (
                      <div className="px-4 py-8 text-center text-gray-500 text-sm">
                        Нет уведомлений
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-200">
                        {notifications.slice(0, 10).map(notification => (
                          <div
                            key={notification.id}
                            onClick={() => markAsRead(notification.id)}
                            className={`px-4 py-3 hover:bg-gray-50 cursor-pointer ${
                              !notification.is_read ? 'bg-blue-50' : ''
                            }`}
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <p className="text-sm font-medium text-gray-900">
                                  {notification.title}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                  {notification.message}
                                </p>
                                <p className="text-xs text-gray-400 mt-1">
                                  {new Date(notification.created_at).toLocaleString('ru-RU')}
                                </p>
                              </div>
                              {!notification.is_read && (
                                <div className="ml-2 w-2 h-2 bg-blue-600 rounded-full"></div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Desktop User Info */}
              <div className="hidden md:flex items-center text-white mr-4">
                <Link
                  to="/profile"
                  className="inline-flex items-center hover:text-primary-200 transition-colors"
                  title="Профиль и смена пароля"
                >
                  <FiUser className="mr-2" />
                  <span className="text-sm">{user.full_name}</span>
                </Link>
                {user.is_admin && (
                  <span className="ml-2 px-2 py-1 bg-yellow-500 text-xs rounded-full">Админ</span>
                )}
              </div>
              {/* Desktop Logout */}
              <button
                onClick={handleLogout}
                className="hidden md:inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 transition-colors"
              >
                <FiLogOut className="mr-2" />
                Выход
              </button>
            </div>
          </div>
          
          {/* Mobile menu */}
          {mobileMenuOpen && (
            <div className="xl:hidden border-t border-gray-700">
              <div className="px-2 pt-2 pb-3 space-y-1">
                <Link
                  to="/"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`${isActive('/')} block px-3 py-2 rounded-md text-base font-medium transition-colors`}
                >
                  <FiHome className="inline mr-2" />
                  Главная
                </Link>
                <Link
                  to="/equipment"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`${isActive('/equipment')} block px-3 py-2 rounded-md text-base font-medium transition-colors`}
                >
                  <FiPackage className="inline mr-2" />
                  Оборудование
                </Link>
                <Link
                  to="/partners"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`${isActive('/partners')} block px-3 py-2 rounded-md text-base font-medium transition-colors`}
                >
                  <FiGlobe className="inline mr-2" />
                  Партнёры
                </Link>
                <Link
                  to="/bookings"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`${isActive('/bookings')} block px-3 py-2 rounded-md text-base font-medium transition-colors`}
                >
                  <FiCalendar className="inline mr-2" />
                  Бронирования
                </Link>
                {user.is_admin && (
                  <Link
                    to="/users"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`${isActive('/users')} block px-3 py-2 rounded-md text-base font-medium transition-colors`}
                  >
                    <FiUsers className="inline mr-2" />
                    Пользователи
                  </Link>
                )}
                {user.is_admin && (
                  <Link
                    to="/notifications"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`${isActive('/notifications')} block px-3 py-2 rounded-md text-base font-medium transition-colors`}
                  >
                    <FiBell className="inline mr-2" />
                    Уведомления
                  </Link>
                )}
                {user.is_admin && (
                  <Link
                    to="/logs"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`${isActive('/logs')} block px-3 py-2 rounded-md text-base font-medium transition-colors`}
                  >
                    <FiFileText className="inline mr-2" />
                    Логи
                  </Link>
                )}
                <Link
                  to="/about"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`${isActive('/about')} block px-3 py-2 rounded-md text-base font-medium transition-colors`}
                >
                  <FiInfo className="inline mr-2" />
                  О программе
                </Link>
                <div className="border-t border-gray-700 pt-2 mt-2">
                  <Link
                    to="/profile"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`${isActive('/profile')} block px-3 py-2 rounded-md text-base font-medium transition-colors`}
                  >
                    <FiSettings className="inline mr-2" />
                    Профиль
                  </Link>
                  <div className="px-3 py-2 text-white text-sm">
                    <FiUser className="inline mr-2" />
                    {user.full_name}
                    {user.is_admin && (
                      <span className="ml-2 px-2 py-1 bg-yellow-500 text-xs rounded-full">Админ</span>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      handleLogout();
                    }}
                    className="w-full text-left px-3 py-2 rounded-md text-base font-medium text-white bg-red-600 hover:bg-red-700 transition-colors"
                  >
                    <FiLogOut className="inline mr-2" />
                    Выход
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Main content */}
      <main className="w-full min-w-0 max-w-[1920px] mx-auto py-4 sm:py-6 px-4 sm:px-6 lg:px-8 xl:px-12 flex-1 pb-16">
        <Outlet />
      </main>

      <AppFooter linkVersion />
    </div>
  );
}

export default Layout;

